import { useState } from 'react';
import type { PrivateView } from '@ringmaster/shared';
import { useRoom } from '../net/useRoom.js';
import { FixedActionBar, fixedPrimaryButtonStyle } from '../ui/FixedActionBar.js';
import { HowToPlayCard } from '../ui/HowToPlay.js';
import { HoloRoleCard } from '../ui/HoloRoleCard.js';
import { ROLE_CARD_DEFS, ROLE_CARD_ART } from '../ui/roleCardDefs.js';
import { Glyph } from '../ui/Glyph.js';
import { ParticleLayer } from '../fx/particles/ParticleLayer.js';
import { BurstLayer } from '../fx/particles/BurstLayer.js';
import { burst } from '../fx/particles/burst.js';
import spotlightCone from '../assets/ornaments/spotlight-cone.webp';

/**
 * Warm the browser cache for the role-card art. Called from the lobby screens
 * so the DEAL flip is instant even on a slow mobile connection.
 */
export function preloadRoleCardArt(): void {
  for (const src of ROLE_CARD_ART) {
    const img = new Image();
    img.src = src;
  }
}

/**
 * RoleRevealScreen — DEAL phase.
 * 1. "How to play" card (3 bullets, dismissible).
 * 2. Press-and-hold the ornate card-back → flips (3D) to the composed role card
 *    ({@link HoloRoleCard}: pointer tilt + glare + blood-moon/role art).
 * 3. Wolves see packmates listed below the card.
 * 4. "I'm ready" → READY intent → the card IMMEDIATELY flips face-down into a
 *    "waiting for the others" state. The player can still press-and-hold to peek
 *    at their own role again during DEAL (they often forget it) — being ready
 *    only hides the button, not the card.
 *
 * Reveal state is owned HERE (controlled HoloRoleCard) because the two DEAL
 * states differ: the first reveal is STICKY (read it, reach the ready button);
 * after readying, a peek is momentary — releasing the card hides it again
 * (shoulder-surf safety). Works with mouse and touch (pointer events).
 */

export function RoleRevealScreen({
  privateView,
  actionOffset = 0,
}: {
  privateView: PrivateView;
  actionOffset?: number;
}) {
  const { send } = useRoom();
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const [revealed, setRevealed] = useState(false);

  // Server-confirmed ready OR locally pressed ready — either way, hide the role.
  const serverReady = privateView.action.type === 'READY' && privateView.action.done;
  const [localReady, setLocalReady] = useState(false);
  const isReady = serverReady || localReady;

  const def = ROLE_CARD_DEFS[privateView.role];

  // On the flip completing, a faction-appropriate burst blooms behind the card.
  function handleReveal() {
    setRevealed(true);
    const at = { x: 0.5, y: 0.46 };
    switch (privateView.role) {
      case 'WEREWOLF':
        burst({ preset: 'bloodBurst', ...at });
        break;
      case 'SEER':
        burst({ preset: 'arcanaWisps', ...at, count: 18 });
        break;
      default:
        burst({ preset: 'goldBurst', ...at });
    }
  }

  function handleReady() {
    // Hide the role immediately (shoulder-surf safety) — don't wait for the
    // server round-trip / phase end.
    setLocalReady(true);
    setRevealed(false);
    send({ type: 'READY' });
  }

  // Onboarding card.
  if (!onboardingDismissed) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #080808 0%, #140002 100%)',
          padding: 'var(--space-5)',
          paddingBottom: 120 + actionOffset,
        }}
      >
        <HowToPlayCard />
        <FixedActionBar offsetBottom={actionOffset}>
          <button
            type="button"
            className="rm-hover-pill"
            onClick={() => setOnboardingDismissed(true)}
            style={fixedPrimaryButtonStyle}
          >
            Got it — reveal my role
          </button>
        </FixedActionBar>
      </div>
    );
  }

  const isPackWolf = privateView.packmates && privateView.packmates.length > 0;

  return (
    <div
      className="game-screen"
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'radial-gradient(120% 90% at 50% 0%, #1a0710 0%, #0a0305 55%, #060204 100%)',
        padding: 'var(--space-5)',
        // Leave room for the fixed "I'm ready" bar (+ host RingmasterBar).
        paddingBottom: 110 + actionOffset,
        gap: 'var(--space-4)',
        overflow: 'hidden',
      }}
    >
      {/* Spotlight shaft from the top — screen blend so it reads as light, not fog. */}
      <img
        src={spotlightCone}
        alt=""
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(120%, 560px)',
          maxHeight: '78%',
          objectFit: 'contain',
          objectPosition: 'top',
          mixBlendMode: 'screen',
          opacity: 0.7,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      {/* Dust motes drifting in the shaft. */}
      <ParticleLayer preset="goldDust" intensity={0.4} slot={0} />
      {/* Faction burst target (mounted here so the bloom lands over the card). */}
      <BurstLayer />

      {/* Prompt above the card */}
      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', minHeight: 56 }}>
        {revealed ? (
          <p
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 15,
              color: 'var(--text-secondary)',
              margin: 0,
            }}
          >
            {isReady ? 'Release to hide' : 'Your secret role'}
          </p>
        ) : isReady ? (
          <p
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 15,
              color: 'var(--text-secondary)',
              margin: 0,
              maxWidth: 260,
            }}
          >
            You&rsquo;re ready — hold the card to peek again.
          </p>
        ) : (
          <>
            <div style={{ fontFamily: 'var(--font-ui)', fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.15 }}>
              Tap and hold
            </div>
            <div style={{ fontFamily: 'var(--font-ui)', fontSize: 18, color: 'var(--text-secondary)', marginTop: 2 }}>
              to see your role
            </div>
          </>
        )}
      </div>

      {/* The ornate card — press-and-hold to reveal; face-down when hidden/ready.
          Reveal is controlled here so the sticky-reveal / hold-to-peek states
          behave correctly. Sized to fit the viewport minus header + fixed bar.
          The card + its 3D transforms MUST stay untouched (quality bar). */}
      <div style={{ position: 'relative', zIndex: 1 }}>
      <HoloRoleCard
        def={def}
        revealed={revealed}
        onReveal={handleReveal}
        onHoldEnd={() => {
          // After readying, peeking is momentary — releasing hides the role
          // again. The initial pre-ready reveal stays sticky.
          if (isReady) setRevealed(false);
        }}
        width="min(300px, 82vw, 34vh)"
      />
      </div>

      {/* Wolf packmates (only when revealed) */}
      {revealed && isPackWolf && (
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            maxWidth: 300,
            background: 'rgba(128, 38, 54, 0.15)',
            border: '1px solid rgba(153,0,0,0.4)',
            borderRadius: 'var(--radius-card)',
            padding: 'var(--space-4) var(--space-5)',
          }}
        >
          <div style={{ color: 'var(--accent-blood-end)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 'var(--space-3)' }}>
            Your pack
          </div>
          {privateView.packmates!.map((pm) => (
            <div
              key={pm.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: '8px 0',
                color: 'var(--text-primary)',
                fontSize: 15,
              }}
            >
              <Glyph name="wolfHead" size={16} tone="blood" />
              <span>{pm.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Ready button — fixed at the bottom, always visible while revealed */}
      {revealed && !isReady && (
        <FixedActionBar offsetBottom={actionOffset}>
          <button type="button" className="rm-hover-pill" onClick={handleReady} style={fixedPrimaryButtonStyle}>
            I&rsquo;m ready
          </button>
        </FixedActionBar>
      )}
    </div>
  );
}
